import * as React from 'react';
import { Box, Button, FormControl, FormHelperText, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { expandShortHex } from '../../utility/preferenceColor';
import { DEFAULT_PREFERENCE_COLOR } from '../../types/Preference';

const HEX3 = /^#[0-9a-f]{3}$/i;
const HEX6 = /^#[0-9a-f]{6}$/i;

const isValidHex = (value: string) => HEX3.test(value) || HEX6.test(value);

const PRESET_COLORS = [
  // Reds
  '#ef5350',
  '#e57373',
  '#f44336',

  // Pinks
  '#f06292',
  '#ec407a',
  '#d81b60',

  // Purples
  '#ba68c8',
  '#ab47bc',
  '#9575cd',

  // Deep Purple / Indigo
  '#7e57c2',
  '#5c6bc0',

  // Blues
  '#64b5f6',
  '#42a5f5',
  '#2196f3',
  '#4fc3f7',

  // Teals / Cyans
  '#4db6ac',
  '#26a69a',
  '#26c6da',

  // Greens
  '#81c784',
  '#66bb6a',
  '#43a047',

  // Lime / Yellow
  '#cddc39',
  '#ffd54f',
  '#ffca28',

  // Oranges
  '#ffb74d',
  '#ff9800',
  '#ff8a65',

  // Browns / Neutrals
  '#a1887f',
  '#8d6e63',
  '#90a4ae',
];

type Props = {
  value?: string;
  onChange: (hex?: string) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
};

export default function PreferenceColorField({
  value,
  onChange,
  label = 'Color',
  helperText = 'Pick a color or enter a hex like #aabbcc',
  disabled,
  size = 'medium',
}: Props) {
  const [text, setText] = useState(value ?? '');
  const [showCustom, setShowCustom] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const expandedValue = useMemo(() => {
    if (!value) return undefined;
    return expandShortHex(value.toLowerCase());
  }, [value]);

  const isPresetSelected = useMemo(() => {
    return !!expandedValue && PRESET_COLORS.includes(expandedValue);
  }, [expandedValue]);

  useEffect(() => {
    setText(value ?? '');
    setShowCustom(!!value && !isPresetSelected);
  }, [value, isPresetSelected]);

  const lowerText = text.toLowerCase();
  const isValid = text === '' || isValidHex(lowerText);

  const previewColor = expandShortHex(isValid && text ? lowerText : undefined) ?? DEFAULT_PREFERENCE_COLOR;

  const colorPickerValue = expandShortHex(isValidHex(lowerText) ? lowerText : DEFAULT_PREFERENCE_COLOR) ?? DEFAULT_PREFERENCE_COLOR;

  const commitIfValid = (raw: string) => {
    const next = raw.toLowerCase();
    if (!next) {
      onChange(undefined);
    } else if (isValidHex(next)) {
      onChange(next);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setText(next);
    commitIfValid(next);
  };

  const handleBlur = () => {
    const trimmed = lowerText.trim();
    if (trimmed !== text) {
      setText(trimmed);
      commitIfValid(trimmed);
    }
  };

  const openPicker = () => {
    if (!disabled) colorInputRef.current?.click();
  };

  const handlePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hex = e.target.value.toLowerCase();
    setText(hex);
    onChange(hex);
  };

  return (
    <FormControl disabled={disabled} fullWidth>
      <Typography variant="caption" sx={{ mb: 0.5 }}>
        {label}
      </Typography>

      {/* Preset color swatches */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, px: 0.5, flexWrap: 'wrap' }}>
        {PRESET_COLORS.map((hex) => {
          const isSelected = expandedValue === hex;

          return (
            <Box
              key={hex}
              role="button"
              tabIndex={disabled ? -1 : 0}
              onClick={() => {
                if (disabled) return;
                onChange(hex);
                setText(hex);
                setShowCustom(false);
              }}
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: hex,
                cursor: disabled ? 'not-allowed' : 'pointer',
                boxShadow: isSelected
                  ? (t) => `
                      inset 0 0 0 2px ${t.palette.common.white},
                      0 0 0 1px ${t.palette.primary.main}
                    `
                  : '0 0 0 1px rgba(0,0,0,0.3)',
              }}
            />
          );
        })}
      </Box>

      {/* Custom color toggle */}
      <Button
        size="small"
        variant="text"
        disabled={disabled}
        onClick={() => setShowCustom((v) => !v)}
        sx={{ alignSelf: 'flex-start', px: 0, mb: showCustom ? 1 : 0 }}
      >
        {showCustom ? 'Hide custom color' : 'Custom color'}
      </Button>

      {/* Custom picker */}
      {showCustom && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box
            role="button"
            aria-label="Open color picker"
            tabIndex={disabled ? -1 : 0}
            onClick={openPicker}
            sx={{
              width: 36,
              height: 28,
              borderRadius: 0.75,
              bgcolor: previewColor,
              border: '1px solid rgba(0,0,0,0.2)',
              flex: '0 0 36px',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          />

          <input
            ref={colorInputRef}
            type="color"
            value={colorPickerValue}
            onChange={handlePickerChange}
            style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }}
            aria-hidden
            tabIndex={-1}
            disabled={disabled}
          />

          <TextField
            value={text}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={text !== '' && !isValid}
            placeholder="#aabbcc"
            size={size}
            slotProps={{ input: { inputProps: { maxLength: 7 } } }}
            sx={{ flex: 1 }}
          />
        </Box>
      )}

      {!isValid && text !== '' ? (
        <FormHelperText error>Invalid hex (e.g., #1a2b3c)</FormHelperText>
      ) : (
        helperText && <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}
